import React, { useEffect, useRef, useState } from 'react'

import {
  Grid,
  Col,
  Heading2,
  Box,
  Body2,
  Color,
  Display,
  AlignItems,
  Space,
  Image,
  JustifyContent,
  Join,
  withCitrusTheme,
  BorderStyle,
  TextAlign,
} from '@gousto-internal/citrus-react'

import { ReferAFriend } from 'routes/OrderConfirmation/components/ReferAFriend'

import { AppPromoV2 } from '../AppPromoV2'
import { BuildYourNextBoxContainer } from '../BuildYourNextBox/BuildYourNextBoxContainer'
import { DiscountBanner } from '../DiscountBanner/DiscountBannerContainer'
import { WhatsComingUpNext } from '../WhatsComingUpNext/WhatsComingUpNext'
import { YourFirstBox } from '../YourFirstBox'
import { getMenuByPeriodId } from '../api/getMenuByPeriodId'
import WelcomeGoustoBox from '../assets/Box_icon.svg'
import { useWelcomeTracking } from '../hooks/useWelcomeTracking'

const RoundedBox = withCitrusTheme(Box, () => ({
  borderRadius: 8,
}))

function renderMobileLayout(
  renderedOrderSummary: React.JSX.Element,
  renderedBuildYourNextBox: React.JSX.Element,
  renderedAppPromo: React.JSX.Element,
  renderedReferAFriend: React.JSX.Element,
  nameFirst: string,
) {
  return (
    <Col size={12} display={['block', 'block', 'none', 'none']}>
      <Col size={12}>
        <Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            paddingBottom={4}
          >
            <Image
              width="130px"
              height="96px"
              src={WelcomeGoustoBox}
              alt="Gousto logo"
              data-testid="welcome-gousto-box-image"
            />
          </Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
          >
            <Heading2 size={9} textAlign={TextAlign.Center}>
              Welcome to Gousto, {nameFirst}!
            </Heading2>
          </Box>
        </Box>
        <Space size={3} />
      </Col>
      <DiscountBanner />
      <Space size={6} />
      <Join with={<Space size={6} />}>
        <Box>{renderedOrderSummary}</Box>
        <RoundedBox
          bg={Color.White}
          paddingV={4}
          paddingH={3}
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_100}
        >
          {renderedBuildYourNextBox}
        </RoundedBox>

        <RoundedBox
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_100}
          bg={Color.White}
          display={Display.Flex}
          paddingV={4}
          paddingH={3}
          data-testing="appPromo"
          justifyContent={JustifyContent.Center}
        >
          {renderedAppPromo}
        </RoundedBox>
        <RoundedBox
          borderWidth={0.5}
          borderStyle={BorderStyle.Solid}
          borderColor={Color.ColdGrey_100}
          bg={Color.White}
          display={Display.Flex}
          paddingV={4}
          paddingH={3}
          data-testing="referAFriendSection"
        >
          {renderedReferAFriend}
        </RoundedBox>
      </Join>
    </Col>
  )
}

function renderTabletAndDesktopLayout(
  renderedOrderSummary: React.JSX.Element,
  renderedBuildYourNextBox: React.JSX.Element,
  renderedAppPromo: React.JSX.Element,
  renderedReferAFriend: React.JSX.Element,
  nameFirst: string,
  userEmail: string,
) {
  return (
    <>
      <Col size={12} display={['none', 'none', 'block', 'block']}>
        <Box>
          <Box
            display={Display.Flex}
            alignItems={AlignItems.Center}
            justifyContent={JustifyContent.Center}
            paddingBottom={4}
          >
            <Image
              width="178px"
              height="124px"
              src={WelcomeGoustoBox}
              alt="Gousto logo"
              data-testid="welcome-gousto-box-image"
            />
          </Box>
          <Heading2 size={9}>Welcome to Gousto, {nameFirst}!</Heading2>
        </Box>
        <Space size={2} />
        <Box>
          <Body2 color={Color.ColdGrey_800}>
            We&apos;ll send your order details shortly, as well as information about your
            subscription to {userEmail}.
          </Body2>
        </Box>
      </Col>
      <Col size={7} display={['none', 'none', 'block', 'block']}>
        <Box>
          <DiscountBanner />
        </Box>
        <Space size={6} />
        <Join with={<Space size={6} />}>
          <RoundedBox
            bg={Color.White}
            paddingV={5}
            paddingH={3}
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
          >
            {renderedBuildYourNextBox}
          </RoundedBox>
          <RoundedBox
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
            bg={Color.White}
            paddingV={3}
            paddingH={3}
            display={['none', 'none', Display.Flex, Display.Flex]}
          >
            {renderedAppPromo}
          </RoundedBox>
          <RoundedBox
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
            bg={Color.White}
            paddingV={3}
            paddingH={3}
            display={['none', 'none', 'none', Display.Flex]}
            alignItems={AlignItems.Center}
            data-testing="referAFriendSection"
          >
            {renderedReferAFriend}
          </RoundedBox>
          <RoundedBox
            borderWidth={0.5}
            borderStyle={BorderStyle.Solid}
            borderColor={Color.ColdGrey_100}
            bg={Color.White}
            paddingV={3}
            paddingH={3}
            display={['none', 'none', Display.Flex, 'none']}
            alignItems={AlignItems.Center}
          >
            {renderedReferAFriend}
          </RoundedBox>
        </Join>
      </Col>
      <Col size={5} display={['none', 'none', 'block', 'block']}>
        <Join with={<Space size={6} />}>
          <Box>{renderedOrderSummary}</Box>
        </Join>
      </Col>
    </>
  )
}

interface WelcomeFirstBoxOrderWrapperProps {
  nameFirst: string
  userEmail: string
  periodId: string
  isSignUpSeamlessFlowIntoPlanningEnabled: boolean
}

export function WelcomeFirstBoxOrderWrapper({
  nameFirst,
  userEmail,
  periodId,
  isSignUpSeamlessFlowIntoPlanningEnabled,
}: WelcomeFirstBoxOrderWrapperProps) {
  const [tertiaryMenu, setTertiaryMenu] = useState<boolean>(false)
  const { trackWelcomePageChanged } = useWelcomeTracking()

  const hasTrackedPageChangeRef = useRef(false)

  useEffect(() => {
    let cancelledCall = false

    async function getUsersMenu() {
      const usersMenuFromPeriodId = await getMenuByPeriodId(periodId)
      if (cancelledCall) return

      setTertiaryMenu(usersMenuFromPeriodId === 'tertiary')

      if (!hasTrackedPageChangeRef.current && usersMenuFromPeriodId) {
        hasTrackedPageChangeRef.current = true
        trackWelcomePageChanged({
          firstOrderWeek: usersMenuFromPeriodId,
          newLocation: window.location.href,
        })
      }
    }

    getUsersMenu()

    return () => {
      cancelledCall = true
    }
  }, [periodId, trackWelcomePageChanged])

  const renderedOrderSummary = <YourFirstBox />

  const renderedBuildYourNextBox = tertiaryMenu ? (
    <WhatsComingUpNext />
  ) : (
    <BuildYourNextBoxContainer />
  )

  const renderedReferAFriend = (
    <ReferAFriend
      isSignUpSeamlessFlowIntoPlanningEnabled={isSignUpSeamlessFlowIntoPlanningEnabled}
    />
  )
  const renderedAppPromo = <AppPromoV2 />

  return (
    <Grid
      paddingH={[3, 5, 3]}
      paddingV={[3, 5, 3]}
      paddingBottom={[3, 5, 3]}
      paddingRight={[3, 5, 3]}
      gap={6}
    >
      {renderMobileLayout(
        renderedOrderSummary,
        renderedBuildYourNextBox,
        renderedAppPromo,
        renderedReferAFriend,
        nameFirst,
      )}
      {renderTabletAndDesktopLayout(
        renderedOrderSummary,
        renderedBuildYourNextBox,
        renderedAppPromo,
        renderedReferAFriend,
        nameFirst,
        userEmail,
      )}
    </Grid>
  )
}
